import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router';
import type { SortDir, TableSort } from './tableSort';

/** One filter on a table (APP-149 D4): the values it takes, and the one that shows everything. */
export interface TableFilter {
  /** Every value it takes. Anything else in the address reads as `default`. */
  values: readonly string[];
  /** The value that filters nothing out ("anyone", "all"); it stays out of the address. */
  default: string;
  /** Its param in the address; the filter's id (after the prefix) when left out. */
  param?: string;
}

/** Where a table keeps its sort and filters in the address. */
export interface TableSortParams {
  /** The sorted column's param (default `sort`). */
  sort?: string;
  /** The direction's param (default `dir`). */
  dir?: string;
  /**
   * For a second table on the page: `prefix: 'late'` reads `lateSort`, `lateDir`, `latePage` and
   * `lateClaim`, as a second pager reads `latePage`.
   */
  prefix?: string;
  /** The pager's param, dropped on every change so the list starts at page 1 (default `page`). */
  page?: string;
}

export interface TableSortOptions<F extends string> {
  /** The order the table opens in (D3): for the queue, Submitted with the oldest first. */
  defaultSort: TableSort;
  /** The columns that sort; a column in the address that isn't one of these is ignored. */
  sortable: readonly string[];
  filters?: Readonly<Record<F, TableFilter>>;
  /** The person's last sort of this table (Keep my sort, D7), for when the address has none. */
  saved?: TableSort | null | undefined;
  /** Called when the person changes the sort, never on load: where Keep my sort saves it. */
  onSorted?: ((sort: TableSort) => void) | undefined;
  params?: TableSortParams;
}

export interface TableSortState<F extends string> {
  sort: TableSort;
  /** Sort by a column (one of `sortable`). */
  setSort: (next: TableSort) => void;
  /** Each filter's value, always one of its `values`. */
  filters: Readonly<Record<F, string>>;
  /** Set a filter; a value it doesn't take sets it back to its default. */
  setFilter: (id: F, value: string) => void;
  clearFilters: () => void;
  /** Back to the table's own order ("Back to oldest first"). */
  resetSort: () => void;
  /** The sort is the table's own (`defaultSort`), not the person's or the address's. */
  isDefaultSort: boolean;
}

interface Names {
  sort: string;
  dir: string;
  page: string;
  filter: (id: string, filter: TableFilter) => string;
}

function namesOf(params: TableSortParams = {}): Names {
  const prefix = params.prefix ?? '';
  const named = (name: string): string =>
    prefix === '' ? name : `${prefix}${name.charAt(0).toUpperCase()}${name.slice(1)}`;
  return {
    sort: params.sort ?? named('sort'),
    dir: params.dir ?? named('dir'),
    page: params.page ?? named('page'),
    filter: (id, filter) => filter.param ?? named(id),
  };
}

const DIRS: readonly string[] = ['asc', 'desc'];

const sameSort = (a: TableSort, b: TableSort): boolean => a.column === b.column && a.dir === b.dir;

function isSort(
  value: TableSort | null | undefined,
  sortable: readonly string[],
): value is TableSort {
  return (
    value !== null &&
    value !== undefined &&
    sortable.includes(value.column) &&
    // A saved sort comes from the server: check its direction too, whatever its type says.
    DIRS.includes(value.dir)
  );
}

/**
 * The sort and filters an address asks for. Strict: a column that doesn't sort falls back to the
 * person's saved sort, then the table's own; a known column with no direction, or one that isn't
 * `asc` or `desc`, sorts ascending, as a heading's first press does; a filter value the filter
 * doesn't take reads as its default. Nothing from the address is used unless it's on the list. A
 * route loader can read the same, as `pagingFromAddress` does for pages.
 */
export function tableSortFromAddress<F extends string>(
  search: URLSearchParams,
  opts: Pick<TableSortOptions<F>, 'defaultSort' | 'sortable' | 'filters' | 'saved' | 'params'>,
): { sort: TableSort; filters: Record<F, string> } {
  const names = namesOf(opts.params);
  const column = search.get(names.sort);
  const dir: SortDir = search.get(names.dir) === 'desc' ? 'desc' : 'asc';
  const fallback = isSort(opts.saved, opts.sortable) ? opts.saved : opts.defaultSort;
  const sort = column !== null && opts.sortable.includes(column) ? { column, dir } : fallback;
  const filters = {} as Record<F, string>;
  for (const [id, filter] of Object.entries<TableFilter>(opts.filters ?? {})) {
    const value = search.get(names.filter(id, filter));
    filters[id as F] = value !== null && filter.values.includes(value) ? value : filter.default;
  }
  return { sort, filters };
}

/**
 * One table's sort and filters, kept in the address (`?sort=claim&dir=desc&claim=mine`) so Back,
 * reload and a shared link keep them (APP-149 D4). With no sort in the address, the table opens at
 * the person's saved sort if there is one (Keep my sort, D7), else its own. Every change is a new
 * history entry, as a new page is (`usePaging`), and starts the list at page 1. The page sorts and
 * filters the whole list with these, then pages it.
 */
export function useTableSort<F extends string = never>(
  opts: TableSortOptions<F>,
): TableSortState<F> {
  const [search, setSearch] = useSearchParams();
  const { defaultSort, sortable, saved } = opts;
  const read = tableSortFromAddress(search, opts);
  // Stable while the values are the same, so a page can memoise its sorted list on them.
  const sortKey = JSON.stringify(read.sort);
  const filterKey = JSON.stringify(read.filters);
  const sort = useMemo(() => JSON.parse(sortKey) as TableSort, [sortKey]);
  const filters = useMemo(() => JSON.parse(filterKey) as Record<F, string>, [filterKey]);

  // The sort the table opens at with none in the address.
  const fallback = isSort(saved, sortable) ? saved : defaultSort;
  // The latest address and options, for setters that keep one identity across renders.
  // (The router's setter changes with every address, so it's kept here too.)
  const latest = useRef({ search, setSearch, sort, filters, opts, fallback });
  useEffect(() => {
    latest.current = { search, setSearch, sort, filters, opts, fallback };
  });

  /** Change the address (a new entry, as a page change is) and start the list at page 1. */
  const write = useCallback((change: (next: URLSearchParams, names: Names) => void) => {
    const { search: current, setSearch: navigate, opts: now } = latest.current;
    const names = namesOf(now.params);
    const next = new URLSearchParams(current);
    change(next, names);
    next.delete(names.page);
    if (next.toString() !== current.toString()) navigate(next);
  }, []);

  const apply = useCallback(
    (next: TableSort) => {
      const { sort: current, fallback: base, opts: now } = latest.current;
      if (sameSort(next, current)) return;
      write((params, names) => {
        // The address names a sort only when it isn't the one the table opens at anyway.
        if (sameSort(next, base)) {
          params.delete(names.sort);
          params.delete(names.dir);
        } else {
          params.set(names.sort, next.column);
          params.set(names.dir, next.dir);
        }
      });
      now.onSorted?.({ column: next.column, dir: next.dir });
    },
    [write],
  );

  const setSort = useCallback(
    (next: TableSort) => {
      if (isSort(next, latest.current.opts.sortable)) apply(next);
    },
    [apply],
  );

  const resetSort = useCallback(() => {
    apply(latest.current.opts.defaultSort);
  }, [apply]);

  const setFilter = useCallback(
    (id: F, value: string) => {
      const { filters: current, opts: now } = latest.current;
      const filter = now.filters?.[id];
      if (filter === undefined) return;
      const chosen = filter.values.includes(value) ? value : filter.default;
      if (chosen === current[id]) return;
      write((params, names) => {
        const param = names.filter(id, filter);
        if (chosen === filter.default) params.delete(param);
        else params.set(param, chosen);
      });
    },
    [write],
  );

  const clearFilters = useCallback(() => {
    const { filters: current, opts: now } = latest.current;
    const set = Object.entries<TableFilter>(now.filters ?? {}).some(
      ([id, filter]) => current[id as F] !== filter.default,
    );
    if (!set) return;
    write((params, names) => {
      for (const [id, filter] of Object.entries<TableFilter>(now.filters ?? {}))
        params.delete(names.filter(id, filter));
    });
  }, [write]);

  return {
    sort,
    setSort,
    filters,
    setFilter,
    clearFilters,
    resetSort,
    isDefaultSort: sameSort(sort, defaultSort),
  };
}
